import { AsyncPipe, DOCUMENT, isPlatformBrowser, NgTemplateOutlet } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ContentChild,
  CUSTOM_ELEMENTS_SCHEMA,
  DestroyRef,
  ElementRef,
  EventEmitter,
  HostBinding,
  inject,
  Inject,
  Input,
  OnDestroy,
  OnInit,
  Output,
  PLATFORM_ID,
  QueryList,
  Renderer2,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { DeviceService } from '@shared/services';
import { IconTypes } from '@shared/v2/models';
import { distinctUntilChanged, map, Observable, of } from 'rxjs';
import Swiper from 'swiper';
import { register } from 'swiper/element/bundle';
import { SwiperOptions } from 'swiper/types';
import { SdvorIconComponent } from '../icon';
import { SwiperService } from './swiper.service';

register();

@Component({
  selector: 'sd-swiper',
  templateUrl: './swiper.component.html',
  styleUrls: ['./swiper.component.scss'],
  imports: [AsyncPipe, NgTemplateOutlet, SdvorIconComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  providers: [SwiperService],
})
export class SdvorSwiperComponent implements OnInit, AfterViewInit, OnDestroy {
  iconTypes = IconTypes;
  private lastEmittedActiveIndex: number | null = null;

  @Input() title: string;

  /** Равная высота слайдов (для выравнивания контента, напр. цен в карточках товаров) */
  @Input() equalHeightSlides = false;

  // eslint-disable-next-line @angular-eslint/no-input-rename
  @Input('items')
  set _items(data: any[] | null) {
    this.items = data ? data.map((item: any) => (item instanceof Observable ? item : of(item))) : [];
  }

  // eslint-disable-next-line @angular-eslint/no-input-rename
  @Input('config')
  set _config(config: SwiperOptions) {
    this.config = config;
    this.swiperComponentService.updateSwiperConfig(config);
  }

  @ViewChild('swiperContainer', { static: true }) swiperContainer: ElementRef;
  @ViewChild('swiperNavigation', { static: true }) swiperNavigation: ElementRef;

  @ContentChild(TemplateRef, { static: true }) template: TemplateRef<any>;
  childElements: QueryList<ElementRef>;

  @HostBinding('style') swiperStyles: string;
  @HostBinding('attr.ngSkipHydration') ngSkipHydration = true;

  @Output() activeIndexChange = new EventEmitter<number>();
  @Output() reachEnd = new EventEmitter<Swiper>();
  @Output() fromEdge = new EventEmitter<Swiper>();
  @Output() swiperInit = new EventEmitter<Swiper>();
  @Output() slidesPerViewChange = new EventEmitter<number>();

  config: SwiperOptions = {};
  resultConfig: SwiperOptions = {};
  items: Observable<any>[] = [];
  swiperSlidesConfig$: Observable<{ slidesPerView: number | 'auto' | undefined; gridRows: number }>;
  private destroyed = false;

  private swiperComponentService: SwiperService = inject(SwiperService);
  private elRef: ElementRef = inject(ElementRef);

  constructor(
    private deviceService: DeviceService,
    private destroyRef: DestroyRef,
    private renderer: Renderer2,
    @Inject(DOCUMENT) private document: Document,
    @Inject(PLATFORM_ID) private platform: any,
  ) {}

  ngOnInit(): void {
    this.resultConfig = {
      init: true,
      direction: 'horizontal',
      touchEventsTarget: 'wrapper',
      initialSlide: 0,
      speed: 300,
      cssMode: false,
      updateOnWindowResize: true,
      resizeObserver: true,
      nested: false,
      createElements: false,
      enabled: true,
      focusableElements: 'input, select, option, textarea, button, video, label',
      width: null,
      height: null,
      preventInteractionOnTransition: false,
      userAgent: null,
      url: null,
      edgeSwipeDetection: false,
      edgeSwipeThreshold: 20,
      autoHeight: false,
      setWrapperSize: false,
      virtualTranslate: false,
      effect: 'slide',
      breakpoints: undefined,
      breakpointsBase: 'window',
      spaceBetween: 0,
      slidesPerView: 1,
      slidesPerGroup: 1,
      slidesPerGroupSkip: 0,
      slidesPerGroupAuto: false,
      centeredSlides: false,
      centeredSlidesBounds: false,
      slidesOffsetBefore: 0,
      slidesOffsetAfter: 0,
      normalizeSlideIndex: true,
      centerInsufficientSlides: false,
      watchOverflow: true,
      roundLengths: false,
      touchRatio: 1,
      touchAngle: 45,
      simulateTouch: true,
      shortSwipes: true,
      longSwipes: true,
      longSwipesRatio: 0.5,
      longSwipesMs: 300,
      followFinger: true,
      allowTouchMove: true,
      threshold: 0,
      touchMoveStopPropagation: false,
      touchStartPreventDefault: true,
      touchStartForcePreventDefault: false,
      touchReleaseOnEdges: false,
      uniqueNavElements: true,
      resistance: true,
      resistanceRatio: 0.85,
      watchSlidesProgress: false,
      grabCursor: false,
      preventClicks: true,
      preventClicksPropagation: true,
      slideToClickedSlide: false,
      loop: false,
      loopAdditionalSlides: 0,
      rewind: false,
      allowSlidePrev: true,
      allowSlideNext: true,
      swipeHandler: null,
      noSwiping: true,
      noSwipingClass: 'swiper-no-swiping',
      passiveListeners: true,
      maxBackfaceHiddenSlides: 10,
      scrollbar: {
        enabled: true,
        draggable: true,
      },
      mousewheel: {
        enabled: true,
        releaseOnEdges: true,
        forceToAxis: true,
      },
      pagination: false,
      navigation: true,
      ...this.config,
      injectStyles: [
        ...this.injectStyles(),
        ...(this.equalHeightSlides ? [this.getEqualHeightSlidesStyles()] : []),
        ...(Array.isArray(this.config?.injectStyles) ? this.config.injectStyles : []),
      ],
      on: {
        activeIndexChange: (swiper: Swiper): void => {
          const index = swiper.realIndex;
          if (this.lastEmittedActiveIndex !== index) {
            this.lastEmittedActiveIndex = index;
            this.activeIndexChange.emit(index);
          }
        },
        reachEnd: (swiper): void => {
          this.reachEnd.emit(swiper);
        },
        fromEdge: (swiper): void => {
          this.fromEdge.emit(swiper);
        },
      },
    };

    this.swiperSlidesConfig$ = this.deviceService.breakpoint$.pipe(
      map((breakpoint) => {
        const width = this.isBrowser()
          ? this.document?.defaultView?.innerWidth
          : this.deviceService.getSize(breakpoint) - 1;

        let slidesPerView = this.resultConfig.slidesPerView;
        let gridRows = this.resultConfig.grid?.rows || 1;

        if (width && this.resultConfig.breakpoints) {
          const sizesAscending = Object.keys(this.resultConfig.breakpoints).sort(
            (a: string, b: string) => Number(a) - Number(b),
          );

          for (const size of sizesAscending) {
            if (width >= Number(size)) {
              const config = this.resultConfig.breakpoints[size];
              if (config.slidesPerView) {
                slidesPerView = config.slidesPerView;
              }
              if (config.grid?.rows) {
                gridRows = config.grid.rows;
              }
            }
          }
        }

        return { slidesPerView: slidesPerView || 'auto', gridRows };
      }),
    );

    this.swiperSlidesConfig$
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        map(({ slidesPerView }) => slidesPerView),
        distinctUntilChanged(),
      )
      .subscribe((slidesPerView) => {
        this.slidesPerViewChange.emit(slidesPerView as number);
      });

    this.swiperSlidesConfig$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((swiperSlidesConfig) => {
      this.swiperStyles = this.getSwiperStyles(swiperSlidesConfig);
    });
  }

  ngAfterViewInit(): void {
    if (!this.isBrowser()) {
      return;
    }

    this.swiperComponentService.initializeSwiper(this.elRef, this.resultConfig).then((swiper) => {
      if (!swiper || this.destroyed) {
        return;
      }

      this.swiperInit.emit(swiper);
      this.attachNavigationIcons();
    });
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    this.swiperComponentService.destroySwiper();
  }

  get isNavigationEnabled(): boolean {
    if (!this.resultConfig.navigation) return false;

    if (this.resultConfig.navigation === true) return true;

    return this.resultConfig.navigation.enabled ?? true;
  }

  getSwiperStyles(swiperSlidesConfig: { slidesPerView: number | 'auto' | undefined; gridRows: number }): string {
    const { slidesPerView, gridRows } = swiperSlidesConfig;
    const styles = {
      '--swiper-space-between': (this.resultConfig.spaceBetween || 0) + 'px',
      '--swiper-flex-wrap': slidesPerView === 1 && gridRows > 1 ? 'wrap' : 'unset',
    };

    return Object.entries(styles)
      .map(([name, value]) => `${name}: ${value};`)
      .join(' ');
  }

  getSlideClasses(swiperSlidesConfig: { slidesPerView: number | 'auto'; gridRows: number }, index: number): string {
    const classes = [];
    const { slidesPerView, gridRows } = swiperSlidesConfig;
    if (gridRows === 1) {
      if ((slidesPerView as number) <= index) {
        classes.push('item-wrapper-hidden');
      } else if (index !== 0) {
        classes.push('item-wrapper-space-left');
      }
    }
    if (slidesPerView === 1 && gridRows > 1) {
      if (index % Math.ceil(this.items.length / gridRows) !== 0) {
        classes.push('item-wrapper-hidden');
      } else if (index !== 0) {
        classes.push('item-wrapper-space-top');
      }
    }

    return classes.join(' ');
  }

  private attachNavigationIcons(): void {
    if (this.resultConfig.navigation === false) {
      return;
    }

    const shadowRoot = this.swiperContainer?.nativeElement?.shadowRoot;
    const iconPrev = this.swiperNavigation?.nativeElement?.querySelector('.swiper-navigation-icon-prev');
    const iconNext = this.swiperNavigation?.nativeElement?.querySelector('.swiper-navigation-icon-next');
    const prevButtonEl = shadowRoot?.querySelector('.swiper-button-prev');
    const nextButtonEl = shadowRoot?.querySelector('.swiper-button-next');

    if (prevButtonEl && nextButtonEl && iconPrev && iconNext) {
      this.renderer.appendChild(prevButtonEl, iconPrev);
      this.renderer.appendChild(nextButtonEl, iconNext);
    }
  }

  private isBrowser(): boolean {
    return isPlatformBrowser(this.platform);
  }

  private getEqualHeightSlidesStyles(): string {
    return `
      .swiper-wrapper {
        align-items: stretch;
      }
      swiper-slide {
        height: auto;
        display: flex;
      }
      swiper-slide > * {
        flex: 1;
        min-height: 0;
      }
    `;
  }

  private injectStyles(): string[] {
    return [
      // Сделали для того, чтобы была возможность добавить тень у элементов, например, при hover эффекте
      // Визуально ничего не меняет
      `
        .swiper {
          padding: 10px;
          margin: -10px;
        }
      `,
      `
        .swiper-button-prev > svg {
          display: none;
        }
        .swiper-button-next > svg {
          display: none;
        }
      `,
      `
        .swiper-button-prev sd-icon {
          width: 100%;
          height: 100%;
        }
        .swiper-button-next sd-icon {
          width: 100%;
          height: 100%;
        }

      `,
      `
        .swiper-scrollbar-drag {
          cursor: pointer;
        }
      `,
      `
        .swiper-container:not(.swiper-container-initialized) .swiper-wrapper {
          flex-wrap: var(--swiper-flex-wrap);
        }
        .swiper-container:not(.swiper-container-initialized)  .item-wrapper {
          width: 100%;
        }
        .swiper-container:not(.swiper-container-initialized) .item-wrapper-space-left {
          margin-left: var(--swiper-space-between);
        }
        .swiper-container:not(.swiper-container-initialized) .item-wrapper-space-top {
          margin-top: var(--swiper-space-between);
        }
        .swiper-container:not(.swiper-container-initialized) .item-wrapper-hidden {
          display: none;
        }
        .swiper-pagination {
          bottom: -12px !important;
        }
        .swiper-pagination-bullet-active {
          background-color: rgba(179, 179, 179, 1);
        }
        .swiper-pagination-bullet {
          height: 4px;
          width: 4px;
        }
      `,
    ];
  }
}
